import React from 'react';
import { useWorldState } from '../components/state/WorldStateContext.jsx';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

const PHASE_COLORS = {
  scheduled: "bg-gray-600",
  boarding: "bg-green-600",
  pushback: "bg-amber-600",
  taxi_out: "bg-blue-600",
  takeoff: "bg-purple-600",
  climb: "bg-purple-600",
  cruise: "bg-cyan-500",
  descent: "bg-amber-600",
  approach: "bg-amber-600",
  landing: "bg-red-600",
  taxi_in: "bg-blue-600",
  arrived: "bg-green-600",
  completed: "bg-gray-600",
};

export default function FlightDebug() {
  const { worldState, createFlight } = useWorldState();
  const flights = worldState.flights;

  const createTestFlight = () => {
    // Helper to create a random test flight
    createFlight({
      origin: "KORD",
      destination: "KLAX",
      aircraftId: "TEST-AC",
      scheduledDeparture: new Date(worldState.gameTime).toISOString(),
      scheduledArrival: new Date(new Date(worldState.gameTime).getTime() + 4 * 3600000).toISOString(),
    });
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0a0a0a', color: '#e5e5e5' }}>
      <div className="max-w-[1600px] mx-auto">

        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 font-orbitron" style={{ color: '#60ffd0' }}>
            Flight Debug Viewer
          </h1>
          <p className="text-lg text-gray-400">
            Live view of all simulated flights (GameLoopEngine).
          </p>
        </div>

        <button
          onClick={createTestFlight}
          className="mb-4 px-4 py-2 rounded hover:opacity-90 transition-opacity font-semibold bg-[#60ffd0] text-black"
        >
          Create Test Flight
        </button>

        <Card className="bg-[#0c0c0c] border-[#1a1a1a]">
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[calc(100vh-250px)] overflow-y-auto">

              {flights.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No active flights to display.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#0f0f0f] border-b border-[#1a1a1a]">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Flight</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Route</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Scheduled</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Phase</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Progress</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-400">Delay</th>
                    </tr>
                  </thead>

                  <tbody>
                    {flights.filter(f => f).map((flight, idx) => {
                      const badgeColor = PHASE_COLORS[flight.phase] || 'bg-gray-600';
                      
                      return (
                        <tr
                          key={flight.id || idx}
                          className="transition-colors hover:bg-white/5 border-b border-[#1a1a1a]"
                        >
                          <td className="px-4 py-3 font-medium text-gray-200">
                            {flight.flightNumber || '-'}
                          </td>

                          <td className="px-4 py-3 text-gray-400">
                            {flight.origin || '-'} - {flight.destination || '-'}
                          </td>

                          <td className="px-4 py-3 text-gray-400">
                            {flight.scheduledDeparture
                              ? format(new Date(flight.scheduledDeparture), 'MMM dd, HH:mm')
                              : '-'}
                          </td>

                          <td className="px-4 py-3">
                            <Badge variant="outline" className="border-gray-700 text-gray-300">
                              {flight.status}
                            </Badge>
                          </td>

                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${badgeColor}`}>
                              {flight.phase}
                            </span>
                          </td>

                          <td className="px-4 py-3 font-mono text-gray-200">
                            {Math.round((flight.progress || 0) * 100)}%
                          </td>

                          <td className="px-4 py-3 text-gray-200">
                            {flight.totalDelayMinutes > 0 ? (
                              <span className="text-red-500">+{flight.totalDelayMinutes}m</span>
                            ) : (
                              <span className="text-green-500">On Time</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}